/* eslint-disable react/no-array-index-key */
import React from 'react';
import Moment from 'moment';
import PropTypes from 'prop-types';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Skeleton from '@mui/material/Skeleton';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import ListItemIcon from '@mui/material/ListItemIcon';
import Menu from '@mui/material/Menu';
import Chip from '@mui/material/Chip';
import {
  useSelector,
  useDispatch,
} from 'react-redux';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import NoteIcon from '@mui/icons-material/NoteAlt';
import BadgeIcon from '@mui/icons-material/Badge';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import EditIcon from '@mui/icons-material/Edit';
import PaidIcon from '@mui/icons-material/Paid';
import NoteAdd from '@mui/icons-material/NoteAdd';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import { UserService } from '@pingum/api-services';

import {
  AutoAwesome,
  ExpandLess,
  ExpandMore,
  Info,
  Visibility,
  VisibilityOff,
  WorkspacePremium,
} from '@mui/icons-material';
import {
  CircularProgress,
  Collapse,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListSubheader,
  Switch,
} from '@mui/material';
import { groupBy } from 'lodash';
import {
  getHorseNoteHighlights,
  updateHorseSuggestion,
} from '../../../../../redux/horse/actions';
import { UPDATE_HIGHLIGHTS_LIST } from '../../../../../redux/horse/types';
import HorseService from '../../../../../services/HorseService';
import { blueGrey } from '../../../../../utils/brandColors';
import Property from '../../../../../components/Property';
import {
  isUserAdmin,
  isUserOwner,
} from '../../../../../utils/tenantAuthenticationHelper';
import QuestriCard from '../../../../../components/QuestriCard';
import NoteItem from '../../../../../components/NoteItem';
import HorseDueDates from '../../../../../components/HorseDueDates';

const getIconForNoteType = (type) => {
  // Note, Medical, FeedChange, LocationChange, ProfileChange, Competition,
  if (type === 'ProfileChange') return <AssignmentIndIcon />;
  if (type === 'FeedChange') return <RestaurantIcon />;
  if (type === 'LocationChange') return <LocationOnIcon />;
  if (type === 'Billing') return <PaidIcon />;
  if (type === 'Medical') return <MedicalServicesIcon />;
  if (type === 'Competition') return <WorkspacePremium />;
  return <NoteIcon />;
};

export const calculateHorseAge = (dob) => {
  // Calculate the difference in milliseconds between the current date and the provided date of birth
  const diffMs = Date.now() - new Date(dob || 0).getTime();
  // Create a new Date object representing the difference in milliseconds and store it in the variable age_dt (age Date object)
  const ageDt = new Date(diffMs);

  // Calculate the absolute value of the difference in years between the age Date object and the year 1970 (UNIX epoch)
  return Math.abs(ageDt.getUTCFullYear() - 1970);
}

const columLayout = {
  columnGap: '18px',
  columnCount: {
    xs: 1,
    sm: 1,
    md: 1,
    lg: 2,
    xl: 3,
  },
  columnWidth: {
    xs: 'unset',
    sm: 'unset',
    md: 'unset',
    lg: 300,
    xl: 300,
  },
};
const columLayoutCondensed = {
  columnGap: '18px',
  columnCount: {
    xs: 1,
    sm: 1,
    md: 1,
    lg: 1,
    xl: 1,
  },
  columnWidth: {
    xs: 'unset',
    sm: 'unset',
    md: 'unset',
    lg: 'unset',
    xl: 'unset',
  },
};

const taskListContainerSX = {
  width: '100%',
  display: {
    xs: 'block',
    md: 'inline-block',
  },
  columnBreakInside: 'avoid',
  marginBottom: {
    xs: '1rem',
    md: '1rem',
  },
};
const taskListContainerCondensedSX = {
  width: '100%',
  display: { xs: 'block' },
  columnBreakInside: 'avoid',
  marginBottom: { xs: '1rem' },
};

const HighlightsCardsComponent = ({
  id,
  onNotesNavigate,
  openAddEditNoteModal,
  handleEditProfile,
  condensed,
}) => {
  const dispatch = useDispatch();

  const [openNoteHighlightMenu, setOpenNoteHighlightMenu] = React.useState(null);
  const [noteHighlightAnchorEl, setNoteHighlightAnchorEl] = React.useState(null);
  const [openSuggestions, setOpenSuggestions] = React.useState(false);
  const [horseSuggestionTasksGrouped, setHorseSuggestionTasksGrouped] = React.useState({});
  const [mappedEventTemplates, setMappedEventTemplates] = React.useState({});

  // Redux state
  const horse = useSelector((state) => state.horse.horse);
  const horseNoteHighlights = useSelector((state) => state.horse.horseNoteHighlights);
  const isFetchingHorseNoteHighlights = useSelector((state) => state.horse.isFetchingHorseNoteHighlights);
  const currentHorseLodging = useSelector((state) => state.horse.currentHorseLodging);
  const horseSuggestion = useSelector((state) => state.horse.horseSuggestion);
  const isFetchingHorseSuggestion = useSelector((state) => state.horse.isFetchingHorseSuggestion);
  const selectedBarn = useSelector((state) => state.barn.selectedBarn);
  const user = useSelector((state) => state.user.user);
  const eventTemplates = useSelector((state) => state.eventTemplate.eventTemplates);

  const roles = UserService.getUserRolesForTenant(
    user,
    selectedBarn.tenant,
  );
  // eslint-disable-next-line no-unused-vars
  const isAdmin = isUserAdmin(roles) || isUserOwner(roles);

  React.useEffect(() => {
    setOpenSuggestions(false)
    dispatch(getHorseNoteHighlights(id));
  }, [id]);

  React.useEffect(() => {
    if (horseSuggestion) {
      setHorseSuggestionTasksGrouped(groupBy(horseSuggestion.recentTaskRecords, 'taskTypeId'));
    }
  }, [horseSuggestion])

  React.useEffect(() => {
    if (eventTemplates) {
      setMappedEventTemplates(groupBy(eventTemplates, 'id'));
    }
  }, [eventTemplates])

  const handleEditClick = (noteToEdit) => {
    openAddEditNoteModal(noteToEdit, null, () => { })
  };

  const handleChangeOrder = (highlightNote, newOrder) => {
    const sortedList = horseNoteHighlights.sort((a, b) => a.highlightOrder - b.highlightOrder).map((n, index) => ({
      ...n,
      highlightOrder: (index + 1),
    }));
    const indexOfCurrentNote = sortedList.findIndex((n) => n.id === highlightNote.id);
    const indexOfReplacementNote = sortedList.findIndex((n) => n.highlightOrder === newOrder);

    if (indexOfCurrentNote >= 0 && indexOfReplacementNote >= 0 && indexOfCurrentNote < indexOfReplacementNote) {
      // need to push values up
      for (let i = (indexOfCurrentNote + 1); i <= indexOfReplacementNote; i += 1) {
        HorseService.updateHorseNote(id, sortedList[i].id, { horseHighlight: i });
        sortedList[i].highlightOrder = i;
      }
      // last update the one we need
      HorseService.updateHorseNote(id, highlightNote.id, { horseHighlight: newOrder });
    } else if (indexOfCurrentNote >= 0 && indexOfReplacementNote >= 0 && indexOfCurrentNote > indexOfReplacementNote) {
      // need to push values down
      for (let i = indexOfReplacementNote; i < indexOfCurrentNote; i += 1) {
        HorseService.updateHorseNote(id, sortedList[i].id, { horseHighlight: i + 2 });
        sortedList[i].highlightOrder = i + 2;
      }
      // last update the one we need
      HorseService.updateHorseNote(id, highlightNote.id, { horseHighlight: newOrder });
    }

    sortedList[indexOfCurrentNote].highlightOrder = newOrder;
    // Then dispatch new list to update redux
    dispatch({
      type: UPDATE_HIGHLIGHTS_LIST,
      horseNoteHighlights: sortedList,
    })
  }

  if (isFetchingHorseNoteHighlights) {
    return (
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
          <Skeleton
            sx={{
              borderRadius: 4,
              minHeight: 325,
            }}
            height="100%"
            width="100%"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
          <Skeleton
            sx={{
              borderRadius: 4,
              minHeight: 325,
            }}
            height="100%"
            width="100%"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
          <Skeleton
            sx={{
              borderRadius: 4,
              minHeight: 325,
            }}
            height="100%"
            width="100%"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
          <Skeleton
            sx={{
              borderRadius: 4,
              minHeight: 325,
            }}
            height="100%"
            width="100%"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
          <Skeleton
            sx={{
              borderRadius: 4,
              minHeight: 325,
            }}
            height="100%"
            width="100%"
          />
        </Grid>
      </Grid>
    )
  }

  return (
    <Grid
      container
      spacing={4}
      sx={{
        px: condensed
          ? 1
          : 0,
      }}
    >
      <Grid
        item
        xs={12}
        md={condensed
          ? 12
          : 6}
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                Horse Profile Fields
              </Typography>
              <Button startIcon={<EditIcon />} size="small" onClick={() => { handleEditProfile() }}>Edit Profile</Button>
            </Box>
          </Grid>
          <Grid
            item
            xs={6}
            md={condensed
              ? 6
              : 4}
          >
            <Box
              sx={{
                backgroundColor: 'white',
                borderRadius: 2,
                px: 1,
                height: '100%',
              }}
            >
              <Property label="USEF ID">
                <Tooltip title={horse.usefId || '-'}>
                  <Chip icon={<BadgeIcon />} label={horse.usefId || '-'} />
                </Tooltip>
              </Property>
            </Box>
          </Grid>

          {
            Object.keys(horse.referenceNumbers || {}).map((num) => (
              <Grid
                item
                key={`reference-num-${num}`}
                xs={6}
                md={condensed
                  ? 6
                  : 4}
              >
                <Box
                  sx={{
                    backgroundColor: 'white',
                    borderRadius: 2,
                    px: 1,
                    height: '100%',
                  }}
                >
                  <Property label={num}>
                    <Tooltip title={horse.referenceNumbers[num] || '-'}>
                      <Chip icon={<BadgeIcon />} label={horse.referenceNumbers[num] || '-'} />
                    </Tooltip>
                  </Property>
                </Box>
              </Grid>
            ))
          }
          <Grid
            item
            xs={6}
            md={condensed
              ? 6
              : 4}
          >
            <Box
              sx={{
                backgroundColor: 'white',
                borderRadius: 2,
                px: 1,
                height: '100%',
              }}
            >
              <Property label="Microchip Number">
                <Tooltip title={horse.microchipNumber || 'No Value'}>
                  <Box>{horse.microchipNumber || '-'}</Box>
                </Tooltip>
              </Property>
            </Box>
          </Grid>
          <Grid
            item
            xs={6}
            md={condensed
              ? 6
              : 4}
          >
            <Box
              sx={{
                backgroundColor: 'white',
                borderRadius: 2,
                px: 1,
                height: '100%',
              }}
            >
              <Property label="Sex">
                <Tooltip title={horse.sex || 'No Value'}><Box>{horse.sex}</Box>
                </Tooltip>
              </Property>
            </Box>
          </Grid>
          <Grid
            item
            xs={6}
            md={condensed
              ? 6
              : 4}
          >
            <Box
              sx={{
                backgroundColor: 'white',
                borderRadius: 2,
                px: 1,
                height: '100%',
              }}
            >
              <Property label="Date of Birth">
                <Tooltip title={(horse.dateOfBirth && `${Moment(horse.dateOfBirth).format('ll')} (${calculateHorseAge(horse.dateOfBirth)} years)`) || 'No Value'}>
                  <Box>{horse.dateOfBirth
                    ? `${Moment(horse.dateOfBirth).format('ll')} (${calculateHorseAge(horse.dateOfBirth)} years)`
                    : '-'}
                  </Box>
                </Tooltip>
              </Property>
            </Box>
          </Grid>
          <Grid
            item
            xs={6}
            md={condensed
              ? 6
              : 4}
          >
            <Box
              sx={{
                backgroundColor: 'white',
                borderRadius: 2,
                px: 1,
                height: '100%',
              }}
            >
              <Property label="Breed">
                <Tooltip title={horse.breed || 'No Value'}>
                  <Box>{
                    horse.breed
                      ? horse.breed
                      : '-'
                  }
                  </Box>
                </Tooltip>
              </Property>
            </Box>
          </Grid>
          <Grid
            item
            xs={6}
            md={condensed
              ? 6
              : 4}
          >
            <Box
              sx={{
                backgroundColor: 'white',
                borderRadius: 2,
                px: 1,
                height: '100%',
              }}
            >
              <Property label="Height (hands)">
                <Tooltip title={horse.hands || 'No Value'}>
                  <Box>{
                    horse.hands
                      ? horse.hands
                      : '-'
                  }
                  </Box>
                </Tooltip>
              </Property>
            </Box>
          </Grid>
          <Grid
            item
            xs={6}
            md={condensed
              ? 6
              : 4}
          >
            <Box
              sx={{
                backgroundColor: 'white',
                borderRadius: 2,
                px: 1,
                height: '100%',
              }}
            >
              <Property label="Color">
                <Tooltip title={horse.color || 'No Value'}>
                  <Box>{
                    horse.color
                      ? horse.color
                      : '-'
                  }
                  </Box>
                </Tooltip>
              </Property>
            </Box>
          </Grid>
          <Grid
            item
            xs={6}
            md={condensed
              ? 6
              : 4}
          >
            <Box
              sx={{
                backgroundColor: 'white',
                borderRadius: 2,
                px: 1,
                height: '100%',
              }}
            >
              <Property label="Stall">
                <Tooltip title={horse.stall || 'No Value'}>
                  <Box>{
                    horse.stall
                      ? horse.stall
                      : '-'
                  }
                  </Box>
                </Tooltip>
              </Property>
            </Box>
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <Box
            sx={{
              mt: 4,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Typography
              gutterBottom
              variant="h5"
              sx={{ fontWeight: 600 }}
            >
              Highlighted Horse Notes
            </Typography>
            <Button startIcon={<EditIcon />} size="small" onClick={(e) => { setNoteHighlightAnchorEl(e.currentTarget); setOpenNoteHighlightMenu(true); }}>Highlight Horse Note</Button>
          </Box>
          <Box
            sx={condensed
              ? columLayoutCondensed
              : columLayout}
          >
            {
              [...horseNoteHighlights.slice().sort((a, b) => a.highlightOrder - b.highlightOrder)].map((note, index) => (
                <Box
                  sx={{
                    p: 0.5,
                    ...(condensed
                      ? taskListContainerCondensedSX
                      : taskListContainerSX),
                  }}
                  key={`horse-note-highlight-${note.id}`}
                >
                  <QuestriCard
                    title={note.type}
                    avatar={getIconForNoteType(note.type)}
                    actionCount={null}
                    actionReplacement={isAdmin && (
                      <Tooltip placement="top" title="Change Display Order">
                        <Select
                          labelId="highlight-order-select"
                          id="demo-select-small"
                          value={index + 1}
                          size="small"
                          sx={{ color: blueGrey[200] }}
                          label="Order"
                          onChange={(e) => handleChangeOrder(note, e.target.value)}
                          variant="standard"
                        >
                          {
                            horseNoteHighlights.map((data, index2) => (index2 + 1)).map((item) => (
                              <MenuItem key={`order-item-${item}`} value={item} disabled={item === (index + 1)}>{item}</MenuItem>
                            ))
                          }
                        </Select>
                      </Tooltip>
                    )}
                  >
                    <Box
                      sx={{
                        pt: 2,
                        pl: 2,
                        pb: 2,
                      }}
                    >
                      <NoteItem
                        isAdmin={isAdmin}
                        note={note}
                        includeHorseInfo={false}
                        hideActions={!isAdmin}
                        includeUserInfo={false}
                        includeTypeChip={false}
                        includeIcons={false}
                        horseId={id}
                        pinnedHorseNoteId={horse.activeNote
                          ? horse.activeNote.id
                          : null}
                        divider={false}
                        onEdit={handleEditClick}
                      />
                    </Box>
                  </QuestriCard>
                </Box>
              ))
            }
          </Box>
          <Box sx={{ mt: 2 }}>
            {
              (!horseNoteHighlights || horseNoteHighlights.length <= 0) && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    maxWidth: 400,
                    mb: 2,
                  }}
                >
                  <Info fontSize="large" color="primary" sx={{ mr: 2 }} />
                  <Typography
                    sx={{
                      fontWeight: 300,
                      fontSize: '12px',
                      textWrap: 'auto',
                    }}
                  >
                    You can show important or highlighted horse notes right on the profile. Create a new note, or select
                    and existing one so the most important information is always visible on your horse.
                  </Typography>
                </Box>
              )
            }
            <Menu
              id="menu-appbar"
              anchorEl={noteHighlightAnchorEl}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              anchorPosition={{
                top: 0,
                left: 0,
              }}
              open={!!openNoteHighlightMenu}
              onClose={() => { setOpenNoteHighlightMenu(false); setNoteHighlightAnchorEl(null); }}
            >
              <MenuItem onClick={() => { openAddEditNoteModal(null); setOpenNoteHighlightMenu(false); setNoteHighlightAnchorEl(null); }}>
                <ListItemIcon>
                  <NoteAdd color="primary" />
                </ListItemIcon>
                Create New Note
              </MenuItem>
              <MenuItem onClick={onNotesNavigate}>
                <ListItemIcon>
                  <NoteIcon color="primary" />
                </ListItemIcon>
                Use Existing Note
              </MenuItem>
            </Menu>
          </Box>
        </Grid>
      </Grid>
      <Grid
        item
        xs={12}
        md={condensed
          ? 12
          : 6}
      >
        <HorseDueDates
          barnHorse={currentHorseLodging}
          isAdmin={isAdmin}
          zIndex={1500}
        />

        {
          horseSuggestion && (
            <Box>
              <Box
                sx={{
                  mt: 4,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <AutoAwesome sx={{ color: '#FF6600 !important' }} />
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 600,
                      ml: 1,
                    }}
                  >
                    Questri Suggestions
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                  }}
                >
                  {
                    isFetchingHorseSuggestion
                      ? (
                        <CircularProgress />
                      )
                      : (
                        <FormControlLabel
                          control={(
                            <Switch
                              size="small"
                              checked={!horseSuggestion.hideAllSuggestions}
                              onChange={() => {
                                dispatch(updateHorseSuggestion(horseSuggestion.id, { hideAllSuggestions: !horseSuggestion.hideAllSuggestions }))
                              }}
                            />
                          )}
                          labelPlacement="start"
                          label={condensed
                            ? 'Show'
                            : 'Show Questri Suggestions'}
                          sx={{
                            mb: 0,
                            mr: 2,
                          }}
                        />

                      )
                  }
                  {
                    !horseSuggestion.hideAllSuggestions && (
                      <IconButton onClick={() => setOpenSuggestions(!openSuggestions)}>{openSuggestions
                        ? <ExpandLess />
                        : <ExpandMore />}
                      </IconButton>
                    )
                  }
                </Box>
              </Box>
              <Collapse in={openSuggestions}>
                <List
                  sx={{
                    width: '100%',
                    maxHeight: 400,
                    bgcolor: 'background.paper',
                    overflow: 'scroll',
                  }}
                  subheader={(
                    <ListSubheader component="div" id="nested-list-subheader">
                      <Typography
                        color="secondary"
                        sx={{
                          fontSize: 14,
                          textWrap: 'auto',
                        }}
                        gutterBottom
                      >
                        {
                          horseSuggestion.hideAllSuggestions
                            ? 'You are currently hiding Questri suggestions for this horse. You will not see suggestions on the planning board.'
                            : ' Questri will suggest tasks to plan for this horse based on previously planned tasks. Plan at least 2 weeks of tasks for this horse to start seeing suggestions from Questri on your planning board.'
                        }
                      </Typography>
                      <Box>Recently Planned Tasks (last 30 days)</Box>
                    </ListSubheader>
                  )}
                >
                  {
                    Object.keys(horseSuggestionTasksGrouped)
                      .sort((a, b) => horseSuggestionTasksGrouped[b].length - horseSuggestionTasksGrouped[a].length)
                      .map((groupedByTaskId) => {
                        const eventTemplate = (mappedEventTemplates[parseInt(groupedByTaskId, 10)] && mappedEventTemplates[parseInt(groupedByTaskId, 10)][0]) || { name: 'Unknown Task' };
                        const taskIsHidden = horseSuggestion.hiddenTaskSuggestions
                          && !!horseSuggestion.hiddenTaskSuggestions.find((t) => t === parseInt(groupedByTaskId, 10));
                        return (
                          <ListItem key={`li-task-${groupedByTaskId}-${taskIsHidden}`} dense>
                            <ListItemText
                              primary={`${eventTemplate.name}${taskIsHidden
                                ? ' (HIDDEN)'
                                : ''}`}
                              secondary={`Used ${horseSuggestionTasksGrouped[groupedByTaskId].length} Time${horseSuggestionTasksGrouped[groupedByTaskId].length > 1
                                ? 's'
                                : ''}`}
                            />
                            <Button
                              size="small"
                              color={taskIsHidden
                                ? 'primary'
                                : 'secondary'}
                              // eslint-disable-next-line no-nested-ternary
                              endIcon={isFetchingHorseSuggestion
                                ? <CircularProgress />
                                : !taskIsHidden
                                  ? <VisibilityOff />
                                  : <Visibility />}
                              onClick={() => {
                                dispatch(updateHorseSuggestion(
                                  horseSuggestion.id,
                                  {
                                    hiddenTaskSuggestions: taskIsHidden
                                      ? horseSuggestion.hiddenTaskSuggestions.filter((t) => t !== parseInt(groupedByTaskId, 10))
                                      : [...horseSuggestion.hiddenTaskSuggestions, parseInt(groupedByTaskId, 10)],
                                  },
                                ))
                              }}
                            >{!taskIsHidden
                              ? 'Hide From Suggestions'
                              : 'Show Suggestions'}
                            </Button>
                          </ListItem>
                        )
                      })
                  }
                </List>
              </Collapse>

            </Box>
          )
        }
      </Grid>
    </Grid>
  );
};

HighlightsCardsComponent.propTypes = {
  id: PropTypes.string.isRequired,
  onNotesNavigate: PropTypes.func.isRequired,
  openAddEditNoteModal: PropTypes.func.isRequired,
  handleEditProfile: PropTypes.func,
  condensed: PropTypes.bool,
};

HighlightsCardsComponent.defaultProps = {
  handleEditProfile: () => { },
  condensed: false,
}

export default HighlightsCardsComponent;
